import { Injectable } from '@angular/core';
import {
  Action,
  createSelector,
  Selector,
  State,
  StateContext,
} from '@ngxs/store';
import { uuid } from 'src/app/shared/uuid';
import { QuestionSubmission } from '../models/question-submission.model';
import { QuizLabelOption, QuizQuestion, QuizQuestionUnion, QuizSceneConfig } from '../models/quiz-question.model';
import { QuizSettingsModel } from '../models/quiz-settings.model';
import { QuizActions } from './quiz.actions';
import { isAnswerCorrect } from '../config/quiz';
import { get } from 'lodash';
import { QuizQuestionType } from '../models/quiz-question-type.enum';
import { QuizLifecycleState } from '../models/quiz-lifecycle.enum';

export type QuizPickMode = 'single' | 'multi' | 'none';

export enum QuizLaunchMode {
  Learning,
  Assessment,
}
export interface QuizStateModel {
  quizModeActive: boolean;
  // locked into quiz mode
  quizModeLocked: boolean;
  // scope of questions
  quizQuestionsMode: 'all' | 'restricted';
  launchMode: QuizLaunchMode;
  questions: QuizQuestionUnion[];
  activeQuestionId: QuizQuestion['id'];
  // in progress response
  activeSubmission: QuestionSubmission;
  // submitted responses
  questionSubmissions: QuestionSubmission[];
  sceneConfig?: QuizSceneConfig;
  quizSettings: QuizSettingsModel;
  pickMode: QuizPickMode;
  lifecycle: QuizLifecycleState;
}

const initialQuizSettings: QuizSettingsModel = {
  enabled: true,
  showSummary: true,
  showExplanation: true,
  showSubmit: true,
  showProgress: true
};

const initialState: QuizStateModel = {
  quizModeActive: false,
  quizModeLocked: false,
  quizQuestionsMode: 'all',
  launchMode: QuizLaunchMode.Learning,
  questions: [],
  activeQuestionId: undefined,
  activeSubmission: undefined,
  questionSubmissions: [],
  quizSettings: initialQuizSettings,
  pickMode: 'single',
  lifecycle: QuizLifecycleState.Question
};

function getQuestionsByChapter(questions: QuizQuestion[], chapterId: string): QuizQuestion[] {
  return questions.filter((q) => q.chapterId === chapterId);
}

function getChaptersByQuestions(questions: QuizQuestion[]): string[] {
  return [
    ...new Set(questions.map((q) => q.chapterId))
  ];
}

function orderQuestionsByChapter(questions: QuizQuestion[], chapterIds: string[]): QuizQuestion[] {
  const allQuestionsByChapter = chapterIds
    .map((chapterId) => getQuestionsByChapter(questions, chapterId));
  const sortedQuestionsByChapter = [].concat(...allQuestionsByChapter);

  return sortedQuestionsByChapter;
}

function orderQuestions(questions: QuizQuestion[]): QuizQuestion[] {
  const chapterIds = getChaptersByQuestions(questions);
  return orderQuestionsByChapter(questions, chapterIds);
}

@State<QuizStateModel>({
  name: 'quiz',
  defaults: initialState,
})
@Injectable()
export class QuizState {

  static getQuizQuestionsByChapter(chapterId: string) {
    return createSelector(
      [QuizState],
      (state: QuizStateModel): QuizQuestion[] => {
        return getQuestionsByChapter(state.questions, chapterId);
      }
    );
  }

  @Selector()
  static getLifecycle(state: QuizStateModel) {
    return state.lifecycle;
  }

  @Selector()
  static getQuizSettings(state: QuizStateModel): QuizSettingsModel {
    return state.quizSettings;
  }

  @Selector()
  static getQuizModeActive(state: QuizStateModel) {
    return state.quizModeActive;
  }

  @Selector()
  static getQuizModeLocked(state: QuizStateModel) {
    return state.quizModeLocked;
  }

  @Selector()
  static getQuizReviewEnabled(state: QuizStateModel): boolean {
    const { quizModeLocked, quizQuestionsMode, questions, quizSettings } = state;

    // disabled: locked + summary disabled
    if (quizModeLocked && !quizSettings.showSummary) {
      return false;
    }

    // disabled: when locked + restricted + single question
    if (quizModeLocked && quizQuestionsMode === 'restricted' && questions.length === 1) {
      return false;
    }

    return true;
  }

  @Selector()
  static globalSceneConfig(state: QuizStateModel) {
    return state.sceneConfig;
  }

  @Selector()
  static hasQuiz(state: QuizStateModel) {
    return state.questions.length > 0;
  }

  @Selector()
  static getQuizEnabled(state: QuizStateModel) {
    return state.quizSettings.enabled !== false;
  }

  @Selector()
  static getAllQuestions(state: QuizStateModel) {
    return state.questions;
  }

  @Selector()
  static getAllSubmissions(state: QuizStateModel) {
    return state.questionSubmissions;
  }

  @Selector()
  static getActiveSubmission(state: QuizStateModel) {
    return state.activeSubmission;
  }

  @Selector()
  static getActiveQuestion(state: QuizStateModel) {
    return state.questions.find(
      (question) => question.id === state.activeQuestionId
    );
  }

  @Selector()
  static getActiveQuestionId(state: QuizStateModel) {
    return state.activeQuestionId;
  }

  @Selector()
  static getActiveQuestionLabels(state: QuizStateModel): QuizLabelOption[] {
    const activeQuestion = state.questions.find(
      (question) => question.id === state.activeQuestionId
    );
    if (activeQuestion && activeQuestion.type === QuizQuestionType.LabelAnatomy) {
      return activeQuestion.data.options;
    } else {
      return [];
    }
  }

  @Selector()
  static getActiveQuestionIndex(state: QuizStateModel) {
    return state.questions.findIndex((question) => question.id === state.activeQuestionId);
  }

  static getQuestionIndex(questionId: string) {
    return createSelector([QuizState], (state: QuizStateModel) => {
      return state.questions.findIndex((question) => question.id === questionId);
    });
  }

  @Selector()
  static getQuestionTotal(state: QuizStateModel) {
    return state.questions.length;
  }

  @Selector()
  static getCorrectAnswerTotal(state: QuizStateModel) {
    const { questionSubmissions, questions } = state;

    const correctSubmissions = questionSubmissions
      .filter((submission) => {
        const question = questions
          .find((value => value.id === submission.questionId));

        return isAnswerCorrect(question, submission.answer);
      });

    return correctSubmissions.length;
  }

  @Selector()
  static getQuestionsRemaining(state: QuizStateModel) {
    const { questions, questionSubmissions } = state;
    return questions.length - questionSubmissions.length;
  }

  @Selector()
  static hasQuestionRemaining(state: QuizStateModel) {
    const { questions, questionSubmissions } = state;
    return questionSubmissions.length < questions.length;
  }

  @Selector()
  static getPickMode(state: QuizStateModel) {
    return state.pickMode;
  }

  @Action(QuizActions.ResetQuizState)
  resetQuizState(context: StateContext<QuizStateModel>, action: QuizActions.ResetQuizState) {
    context.setState({
      ...initialState
    });
  }

  // pass chapter Id as a parameter to this selector
  @Action(QuizActions.SetQuizState)
  setQuizState(context: StateContext<QuizStateModel>, { quizState }: QuizActions.SetQuizState) {
    context.setState({
      ...initialState,
      ...quizState
    });
  }

  @Action(QuizActions.SetQuizSettings)
  setQuizSettings(context: StateContext<QuizStateModel>, { quizSettings }: QuizActions.SetQuizSettings) {
    context.patchState({
      quizSettings
    });
  }

  @Action(QuizActions.SetQuizActive)
  setQuizActive(
    context: StateContext<QuizStateModel>,
    action: QuizActions.SetQuizActive
  ) {
    context.patchState({
      quizModeActive: action.active,
      activeQuestionId: action.active === false ? undefined : context.getState().activeQuestionId
    });
  }

  @Action(QuizActions.SetQuizEnabled)
  setQuizEnabled(context: StateContext<QuizStateModel>, action: QuizActions.SetQuizEnabled) {
    const quizSettings = context.getState().quizSettings;
    context.patchState({
      quizSettings: {
        ...quizSettings,
        enabled: action.enabled
      }
    });
  }

  @Action(QuizActions.SetQuizLocked)
  setQuizLocked(context: StateContext<QuizStateModel>, action: QuizActions.SetQuizLocked) {
    context.patchState({
      quizModeLocked: action.locked
    });
  }

  @Action(QuizActions.SetQuizQuestionsMode)
  setQuizQuestionsMode(context: StateContext<QuizStateModel>, action: QuizActions.SetQuizQuestionsMode) {
    context.patchState({
      quizQuestionsMode: action.mode
    });
  }

  @Action(QuizActions.CopyQuestions)
  copyQuestions(context: StateContext<QuizStateModel>, { sourceChapterId, targetChapterId }: QuizActions.CopyQuestions) {
    const { questions } = context.getState();
    const sourceChapterQuestions = getQuestionsByChapter(context.getState().questions, sourceChapterId);
    const targetChapterQuestions = sourceChapterQuestions
      .map((q) => {
        // re-key ID fields
        return {
          ...JSON.parse(JSON.stringify(q)),
          id: uuid.idWithPrefix('question'),
          chapterId: targetChapterId
        };
      });
    const targetQuestions = [...questions, ...targetChapterQuestions];

    context.patchState({
      questions: orderQuestions(targetQuestions)
    });
  }

  @Action(QuizActions.ReorderQuestions)
  reorderQuestions(context: StateContext<QuizStateModel>, { chapterIds }: QuizActions.ReorderQuestions) {
    context.patchState({
      questions: orderQuestionsByChapter(context.getState().questions, chapterIds)
    });
  }

  @Action(QuizActions.AddQuestion)
  addQuestion(
    context: StateContext<QuizStateModel>,
    action: QuizActions.AddQuestion
  ) {
    const questions = [...context.getState().questions, action.question];
    context.patchState({
      questions: orderQuestions(questions)
    });
  }

  @Action(QuizActions.DeleteQuestion)
  deleteQuestion(
    context: StateContext<QuizStateModel>,
    action: QuizActions.DeleteQuestion
  ) {
    const questions = context
      .getState()
      .questions.filter((question) => question.id !== action.id);
    context.patchState({
      questions: orderQuestions(questions)
    });
  }
  
  @Action(QuizActions.SetQuestions)
  setQuestions(context: StateContext<QuizStateModel>, { questions }: QuizActions.SetQuestions) {
    context.patchState({
      questions: orderQuestions(questions)
    });
  }

  @Action(QuizActions.SetQuestion)
  setQuestion(
    context: StateContext<QuizStateModel>,
    action: QuizActions.SetQuestion
  ) {
    const questions = context.getState().questions.map((question) => {
      if (question.id === action.question.id) {
        return action.question;
      }
      return question;
    });
    context.patchState({
      questions,
    });
  }

  @Action(QuizActions.SubmitQuestionAnswer)
  submitQuestionAnswer(
    context: StateContext<QuizStateModel>,
    action: QuizActions.SubmitQuestionAnswer
  ) {
    // higher level business logic will need to decide if a quiz question can be answered twice.
    // that decision is not a concern of this action handler.

    const questionSubmissions = [...context.getState().questionSubmissions];
    const index = questionSubmissions.findIndex(
      (submission) => submission.questionId === action.submission.questionId
    );
    const questionAlreadyAnswered = index > -1;

    if (questionAlreadyAnswered) {
      questionSubmissions[index] = action.submission;
    } else {
      questionSubmissions.push(action.submission);
    }

    context.patchState({
      questionSubmissions,
    });
  }

  @Action(QuizActions.NextQuestion)
  nextQuestion(context: StateContext<QuizStateModel>) {
    const { questions, activeQuestionId } = context.getState();
    const currIdx = context
      .getState()
      .questions.findIndex((question) => question.id === activeQuestionId);
    const nextId = questions[currIdx + 1]?.id || undefined;

    if (nextId === undefined) {
      return;
    }

    context.patchState({
      activeQuestionId: nextId,
    });
  }

  @Action(QuizActions.PreviousQuestion)
  previousQuestion(context: StateContext<QuizStateModel>) {
    const { questions, activeQuestionId } = context.getState();
    const currIdx = context
      .getState()
      .questions.findIndex((question) => question.id === activeQuestionId);
    const nextId = questions[currIdx - 1]?.id || undefined;

    if (nextId === undefined) {
      return;
    }

    context.patchState({
      activeQuestionId: nextId,
    });
  }

  @Action(QuizActions.ClearSubmissions)
  clearSubmissions(context: StateContext<QuizStateModel>) {
    context.patchState({ questionSubmissions: [] });
  }

  @Action(QuizActions.SetActiveQuestion)
  setActiveQuestion(
    context: StateContext<QuizStateModel>,
    action: QuizActions.SetActiveQuestion
  ) {
    context.patchState({ activeQuestionId: action.questionId });
  }

  @Action(QuizActions.SetQuizPickMode)
  setQuizPickMode(context: StateContext<QuizStateModel>, { pickMode }: QuizActions.SetQuizPickMode) {
    context.patchState({
      pickMode
    });
  }

  @Action(QuizActions.SetActiveSubmission)
  setActiveSubmission(context: StateContext<QuizStateModel>, { submission }: QuizActions.SetActiveSubmission) {
    context.patchState({
      activeSubmission: submission
    });
  }

  @Action(QuizActions.SetQuizLifecycle)
  setQuizLifecycle(context: StateContext<QuizStateModel>, action: QuizActions.SetQuizLifecycle) {
    context.patchState({
      lifecycle: action.lifecycle
    });
  }

}

